import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * Query helper for MDM collections filtered by outlet
 * All MDM operations are scoped to terminals linked to the selected outlet
 */

/**
 * Get all terminals linked to an outlet
 * @param {string} outletId - The ID of the outlet
 * @returns {Promise<Array>} Array of terminal IDs
 */
export const getOutletTerminals = async (outletId) => {
  try {
    const q = query(
      collection(db, 'device_status'),
      where('outletId', '==', outletId)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[MDM] Error fetching outlet terminals:', error);
    return [];
  }
};

/**
 * Subscribe to terminals for an outlet (real-time)
 * @param {string} outletId - The ID of the outlet
 * @param {Function} callback - Callback function with terminal data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToOutletTerminals = (outletId, callback) => {
  const q = query(
    collection(db, 'device_status'),
    where('outletId', '==', outletId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const terminals = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(terminals);
  }, (error) => {
    console.error('[MDM] Error in terminal subscription:', error);
    callback([]);
  });
};

/**
 * Get policies for an outlet
 * @param {string} outletId - The ID of the outlet
 * @returns {Promise<Array>} Array of policies
 */
export const getOutletPolicies = async (outletId) => {
  try {
    // Get policies that target this outlet or are global
    const q = query(
      collection(db, 'mdm_policies'),
      where('targetOutlets', 'array-contains', outletId)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[MDM] Error fetching outlet policies:', error);
    return [];
  }
};

/**
 * Get commands for an outlet's terminals
 * @param {string} outletId - The ID of the outlet
 * @returns {Promise<Array>} Array of commands
 */
export const getOutletCommands = async (outletId) => {
  try {
    // Get all terminal IDs for this outlet
    const terminals = await getOutletTerminals(outletId);
    const terminalIds = terminals.map(t => t.id);
    
    if (terminalIds.length === 0) {
      return [];
    }
    
    // Get commands for these terminals
    const q = query(
      collection(db, 'remote_commands'),
      where('terminalId', 'in', terminalIds.slice(0, 10)) // Firestore limit: 10 items in 'in' query
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[MDM] Error fetching outlet commands:', error);
    return [];
  }
};

/**
 * Get logs for an outlet's terminals
 * @param {string} outletId - The ID of the outlet  
 * @param {number} limit - Maximum number of logs to fetch
 * @returns {Promise<Array>} Array of logs
 */
export const getOutletLogs = async (outletId, limit = 100) => {
  try {
    // Get all terminal IDs for this outlet
    const terminals = await getOutletTerminals(outletId);
    const terminalIds = terminals.map(t => t.id);
    
    if (terminalIds.length === 0) {
      return [];
    }
    
    // Get logs for these terminals
    const q = query(
      collection(db, 'terminal_logs'),
      where('terminalId', 'in', terminalIds.slice(0, 10)) // Firestore limit: 10 items in 'in' query
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[MDM] Error fetching outlet logs:', error);
    return [];
  }
};

/**
 * Subscribe to real-time updates for outlet devices
 * @param {string} outletId - The ID of the outlet
 * @param {Function} callback - Callback function with device data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToOutletDevices = (outletId, callback) => {
  const q = query(
    collection(db, 'device_status'),
    where('outletId', '==', outletId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const devices = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(devices);
  }, (error) => {
    console.error('[MDM] Error in devices subscription:', error);
    callback([]);
  });
};
